import { supabase } from './supabase';

export type DirectoryAccount = { subjectId: string; displayName: string; openWorkCount: number | null };
export type DirectoryCursor = { query: string; name: string; id: string };
export type DirectoryPage = { items: DirectoryAccount[]; nextCursor: DirectoryCursor | null };
const PAGE_SIZE = 50;

export const readDirectoryCursor = (params: URLSearchParams): DirectoryCursor | null => {
  const raw = params.get('ac');
  if (!raw || raw.length > 4096) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (value && typeof value === 'object' && 'query' in value && value.query === (params.get('q') ?? '').trim()
      && 'name' in value && typeof value.name === 'string' && value.name.length <= 255
      && 'id' in value && typeof value.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.id)) {
      return { query: String(value.query), name: value.name, id: value.id };
    }
  } catch { /* Invalid position has no authority; safely restart the page. */ }
  return null;
};

export const accountListFilters = (params: URLSearchParams): string => {
  const filters = new URLSearchParams();
  const query = (params.get('q') ?? '').slice(0, 200);
  if (query) filters.set('q', query);
  if (params.get('mine') === '1') filters.set('mine', '1');
  const cursor = readDirectoryCursor(params);
  if (cursor) filters.set('ac', JSON.stringify(cursor));
  return filters.size ? `?${filters}` : '';
};

/** B identities, not a CRM portfolio. Every RPC re-applies caller RLS. */
/** `mine`: only the HubSpot companies owned by the signed-in user (« Mes comptes »). */
export const listDominoAccounts = async (query: string, cursor: DirectoryCursor | null = null, mine = false): Promise<DirectoryPage> => {
  if (query.trim().length > 200 || (cursor && cursor.query !== query.trim())) throw new Error('La recherche a changé. Repartez de la première page.');
  const { data, error } = await supabase.rpc('list_accounts_b', {
    p_query: query.trim(), p_after_name: cursor?.name ?? null,
    p_after_id: cursor?.id ?? null, p_limit: PAGE_SIZE + 1, p_mine: mine,
  });
  if (error || !Array.isArray(data)) throw new Error('Les comptes n’ont pas pu être chargés.');
  const rows: unknown[] = data;
  const items = rows.slice(0, PAGE_SIZE).map((row): DirectoryAccount => {
    if (!row || typeof row !== 'object' || !('subject_id' in row) || typeof row.subject_id !== 'string'
      || !('display_name' in row) || typeof row.display_name !== 'string'
      || !('open_work_count' in row) || (row.open_work_count !== null && (typeof row.open_work_count !== 'number' || !Number.isInteger(row.open_work_count) || row.open_work_count < 0))) {
      throw new Error('La liste des comptes n’a pas pu être lue.');
    }
    return { subjectId: row.subject_id, displayName: row.display_name, openWorkCount: row.open_work_count };
  });
  const last = items.at(-1);
  return { items, nextCursor: rows.length > PAGE_SIZE && last ? { query: query.trim(), name: last.displayName, id: last.subjectId } : null };
};
